import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trophy, Skull } from "lucide-react";
import { DriverPortrait } from "@/components/DriverDesigner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MOVES = [
  { id: "jab",   label: "Jab",   subtitle: "Fast · 8 dmg",            color: "#34C759" },
  { id: "cross", label: "Cross", subtitle: "Heavy · 15 dmg",          color: "#FFCC00" },
  { id: "hook",  label: "Hook",  subtitle: "22 dmg · 30% miss",       color: "#FF6B35" },
  { id: "block", label: "Block", subtitle: "−80% incoming",           color: "#1E90FF" },
  { id: "dodge", label: "Dodge", subtitle: "50% avoid",                color: "#9966FF" },
];

/**
 * Boxing boss fight modal.
 *
 * Props:
 *   cityId         — the city we're fighting in
 *   driver         — current driver appearance (skin/hair/shirt/style)
 *   onClose()      — close the modal (defeat or quit)
 *   onVictory(rewards) — fight ended in player win; payload has rewards
 *
 * Server is authoritative — we POST every move and render whatever the
 * server returns. No client-side HP tracking shenanigans.
 */
export default function BossFightModal({ cityId, driver, onClose, onVictory }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  // Boot the fight
  useEffect(() => {
    if (!cityId) return;
    (async () => {
      try {
        const r = await axios.post(`${BACKEND_URL}/api/boxing/start`, { city_id: cityId }, { withCredentials: true });
        setState(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't start fight");
        onClose?.();
      }
    })();
  }, [cityId, onClose]);

  const playMove = useCallback(async (move) => {
    if (!state || state.ended || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/boxing/round`, {
        fight_id: state.fight_id, move,
      }, { withCredentials: true });
      setState(r.data);
      if (r.data.ended && r.data.winner === "player") {
        onVictory?.(r.data.rewards);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    } finally {
      setBusy(false);
    }
  }, [state, busy, onVictory]);

  if (!state) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="boss-fight-loading">
        <div className="text-white/50 text-[11px] tracking-[0.3em] uppercase">Stepping into the ring…</div>
      </div>
    );
  }

  const playerHpPct = Math.max(0, state.player_hp);
  const bossHpMax = state.boss.hp;
  const bossHpPct = (state.boss_hp / bossHpMax) * 100;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center px-2 py-4 bg-black/85 backdrop-blur-md overflow-y-auto"
      data-testid="boss-fight-modal"
    >
      <div className="w-full max-w-3xl bg-black/95 border-2 border-[#FF3B30] flex flex-col">
        {/* Title bar */}
        <div className="text-center py-3 border-b-2 border-[#FF3B30] bg-[#FF3B30]/10">
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#FF3B30] uppercase">Boxing Boss Fight</div>
          <div className="font-heading text-2xl uppercase tracking-tight">
            {state.boss.name}
            <span className="text-[#FFCC00]"> vs </span>
            You
          </div>
          <div className="text-[11px] text-white/60">{state.boss.title} · Tier {state.boss.tier}</div>
        </div>

        {/* Arena — both fighters + HP bars */}
        <div className="grid grid-cols-2 items-center gap-4 px-4 py-6 sm:py-8">
          {/* Player */}
          <div className="flex flex-col items-center gap-3" data-testid="boss-fight-player">
            <DriverPortrait driver={driver} size={96} dataTestId="boss-fight-player-portrait" />
            <HpBar value={playerHpPct} max={100} label="YOU" color="#34C759" testid="boss-fight-player-hp" />
          </div>
          {/* Boss */}
          <div className="flex flex-col items-center gap-3" data-testid="boss-fight-boss">
            <BossPortrait boss={state.boss} size={96} />
            <HpBar value={state.boss_hp} max={bossHpMax} label={state.boss.name.toUpperCase()} color="#FF3B30" testid="boss-fight-boss-hp" />
          </div>
        </div>

        {/* Round log */}
        <div className="border-t border-white/10 max-h-[120px] overflow-y-auto p-3 space-y-1 font-mono text-[11px] text-white/60" data-testid="boss-fight-log">
          {state.log.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>

        {/* End-of-fight summary OR move grid */}
        {state.ended ? (
          <div className="border-t-2 border-[#FFCC00]/40 p-5 text-center" data-testid="boss-fight-result">
            {state.winner === "player" ? (
              <>
                <Trophy className="w-10 h-10 text-[#FFCC00] mx-auto mb-2" />
                <div className="font-heading text-2xl text-[#FFCC00] uppercase">Knockout!</div>
                {state.rewards && (
                  <div className="text-sm text-white/80 mt-2 space-y-1">
                    <div>+{state.rewards.currency.toLocaleString()}¢ bonus</div>
                    <div>Badge earned: <b className="text-[#FFCC00]">{state.rewards.badge}</b></div>
                    {state.rewards.newly_unlocked.length > 0 && (
                      <div>
                        New cities unlocked:{" "}
                        <b className="text-[#34C759]">
                          {state.rewards.newly_unlocked.join(", ")}
                        </b>
                      </div>
                    )}
                    {state.rewards.world_champion_earned && (
                      <div className="text-[#FFCC00] font-heading mt-3">
                        🏆 WORLD CHAMPION badge unlocked + 10,000¢
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <Skull className="w-10 h-10 text-[#FF3B30] mx-auto mb-2" />
                <div className="font-heading text-2xl text-[#FF3B30] uppercase">You got KO'd</div>
                <div className="text-sm text-white/60 mt-2">{state.boss.name} wins this round. Try again from the streets.</div>
              </>
            )}
            <button
              onClick={onClose}
              data-testid="boss-fight-close"
              className="mt-5 px-6 py-2 bg-[#FFCC00] text-black font-heading uppercase text-sm tracking-wider"
            >
              Continue
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 p-3 border-t-2 border-white/10">
            {MOVES.map((m) => (
              <button
                key={m.id}
                onClick={() => playMove(m.id)}
                disabled={busy}
                data-testid={`boss-fight-move-${m.id}`}
                className="flex flex-col items-center gap-0.5 p-3 border-2 transition-colors hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ borderColor: m.color, color: m.color }}
              >
                <span className="font-heading text-base uppercase tracking-tight">{m.label}</span>
                <span className="text-[9px] text-white/50 tracking-wide">{m.subtitle}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function HpBar({ value, max, label, color, testid }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div className="w-full max-w-[180px]" data-testid={testid}>
      <div className="flex items-baseline justify-between text-[10px] tracking-[0.25em] uppercase mb-1">
        <span className="text-white/60">{label}</span>
        <span className="font-mono text-white/80">{value}/{max}</span>
      </div>
      <div className="h-3 bg-white/10 border border-white/10 overflow-hidden">
        <div className="h-full transition-all duration-200" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

/**
 * Quick boss portrait — re-uses the driver palette idea but in a single
 * tone to give each boss a distinct look. Not tied to the player driver.
 */
function BossPortrait({ boss, size = 96 }) {
  const canvasRef = React.useRef(null);
  React.useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    cvs.width = size * dpr; cvs.height = size * dpr;
    cvs.style.width = `${size}px`; cvs.style.height = `${size}px`;
    ctx.scale(dpr, dpr);
    ctx.imageSmoothingEnabled = false;

    const G = 16;
    const px = size / G;
    const skin = boss.skin || "#D8AC85";
    // Background varies by tier (tougher = darker / redder)
    const tierGlow = ["#1F1F1F", "#2A1A1A", "#311010", "#3A0808", "#440000"][Math.min(4, boss.tier - 1)];
    const grad = ctx.createRadialGradient(size / 2, size * 0.4, 4, size / 2, size / 2, size / 1.4);
    grad.addColorStop(0, tierGlow);
    grad.addColorStop(1, "#0A0A0A");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);

    const cell = (gx, gy, c) => { ctx.fillStyle = c; ctx.fillRect(gx * px, gy * px, px, px); };
    const block = (x, y, w, h, c) => {
      for (let i = x; i < x + w; i++) for (let j = y; j < y + h; j++) cell(i, j, c);
    };
    // Head
    block(5, 3, 6, 4, skin);
    // Beard
    block(5, 6, 6, 1, "#1A1A1A");
    // Eyes — angry brow
    cell(6, 4, "#1A1A1A"); cell(9, 4, "#1A1A1A");
    block(5, 3, 6, 1, "#1A1A1A"); // shadow brow
    // Bald head highlight on tier 1; spiky hair on tiers 4-5
    if (boss.tier >= 4) {
      cell(5, 2, "#1A1A1A"); cell(7, 1, "#1A1A1A"); cell(9, 1, "#1A1A1A"); cell(10, 2, "#1A1A1A");
    }
    // Neck
    block(7, 7, 2, 1, skin);
    // Massive shoulders / black tank top
    block(3, 8, 10, 4, "#1A1A1A");
    // Boxing gloves — red
    block(2, 9, 1, 2, "#FF3B30");
    block(13, 9, 1, 2, "#FF3B30");
    // Belt
    block(3, 12, 10, 1, boss.tier >= 4 ? "#FFCC00" : "#5C3B22");
  }, [boss, size]);
  return <canvas ref={canvasRef} data-testid="boss-fight-boss-portrait" className="border border-[#FF3B30]/30" />;
}
